import { useCallback, useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { m } from "framer-motion";
import {
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock,
  Search,
  ShieldCheck,
  User,
  XCircle,
} from "lucide-react";

import { OutcomeState } from "@/components/account/outcome-state";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button-variants";
import { Card, CardContent } from "@/components/ui/card";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { Skeleton } from "@/components/ui/skeleton";

interface MyClaim {
  id: string;
  entityType: string;
  status: string;
  verificationMethod: string | null;
  createdAt: string;
  reviewedAt: string | null;
  entityName: string;
  entityCity: string;
  providerId?: string | null;
  facilityId?: string | null;
  businessId?: string | null;
}

type ClaimStatus = "pending" | "approved" | "rejected";

interface ClaimsSearch {
  /** Status filter — drives the chip strip. */
  status?: ClaimStatus;
}

const STATUS_FILTERS: ("all" | ClaimStatus)[] = ["all", "pending", "approved", "rejected"];

/**
 * "My claims" — list of provider/facility ownership claims the user has
 * submitted. 1:1 port of legacy src/app/[locale]/[country]/dashboard/claims/
 * page.tsx against GET /api/claim/my-claims, plus a URL-driven ?status= chip
 * strip (claims are fetched once and filtered client-side so the chips can
 * show counts).
 */
export const Route = createFileRoute("/_authenticated/dashboard/claims")({
  validateSearch: (search: Record<string, unknown>): ClaimsSearch => {
    const raw = typeof search.status === "string" ? search.status : undefined;
    return {
      status: raw === "pending" || raw === "approved" || raw === "rejected" ? raw : undefined,
    };
  },
  component: MyClaimsPage,
});

function MyClaimsPage() {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const { status } = Route.useSearch();
  const navigate = useNavigate();
  const activeStatus = status ?? "all";

  const [claims, setClaims] = useState<MyClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch.get<MyClaim[]>("/api/claim/my-claims");
      setClaims(res);
      setErrored(false);
    } catch {
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setStatus = (next: "all" | ClaimStatus) =>
    void navigate({
      to: "/dashboard/claims",
      search: next === "all" ? {} : { status: next },
    });

  const filtered = useMemo(
    () => (activeStatus === "all" ? claims : claims.filter((c) => c.status === activeStatus)),
    [claims, activeStatus],
  );

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: claims.length };
    for (const cl of claims) c[cl.status] = (c[cl.status] ?? 0) + 1;
    return c;
  }, [claims]);

  // Unknown statuses fall back to pending — same as legacy statusConfig.
  const statusMeta = (claimStatus: string) => {
    switch (claimStatus) {
      case "approved":
        return {
          icon: <CheckCircle2 aria-hidden />,
          label: t`Approved`,
          variant: "success" as const,
        };
      case "rejected":
        return {
          icon: <XCircle aria-hidden />,
          label: t`Rejected`,
          variant: "destructive" as const,
        };
      default:
        return {
          icon: <Clock aria-hidden />,
          label: t`Pending Review`,
          variant: "warning" as const,
        };
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>My Claims</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Track the status of your profile claim requests</Trans>
        </p>
      </header>

      {loading ? (
        <ClaimsSkeleton />
      ) : errored ? (
        <LoadErrorState title={<Trans>Could not load your claims.</Trans>} onRetry={refresh} />
      ) : claims.length === 0 ? (
        <OutcomeState
          className="border-border bg-card rounded-xl border py-10"
          icon={ShieldCheck}
          title={<Trans>No claims yet</Trans>}
          description={
            <Trans>
              Find your profile in search and click "Claim this profile" to get started.
            </Trans>
          }
          actions={
            <Link to="/find" className={buttonVariants({ size: "touch" })}>
              <Search className="size-4" aria-hidden />
              <Trans>Find my profile</Trans>
            </Link>
          }
        />
      ) : (
        <>
          {/* Status filter chips */}
          <div className="flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map((filter) => {
              const active = activeStatus === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setStatus(filter)}
                  className={cn(
                    "inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                    "focus-visible:ring-primary/40 focus:outline-none focus-visible:ring-2",
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-card text-muted-foreground hover:bg-muted/50",
                  )}
                >
                  {filter === "all" ? <Trans>All</Trans> : null}
                  {filter === "pending" ? <Trans>Pending Review</Trans> : null}
                  {filter === "approved" ? <Trans>Approved</Trans> : null}
                  {filter === "rejected" ? <Trans>Rejected</Trans> : null}
                  <span
                    className={cn(
                      "rounded-full px-1.5 text-[11px] font-bold",
                      active ? "bg-primary/20" : "bg-muted",
                    )}
                  >
                    {counts[filter] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <div className="border-border bg-card flex flex-col items-center gap-2 rounded-xl border py-12 text-center">
              <ShieldCheck className="text-muted-foreground/40 size-8" aria-hidden />
              <p className="text-muted-foreground text-sm">
                <Trans>No claims match this filter.</Trans>
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((claim, i) => {
                const meta = statusMeta(claim.status);
                const workspaceLink = claimWorkspaceLink(claim);
                const entityName = claim.entityName;
                return (
                  <m.div
                    key={claim.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Card className="relative transition-shadow hover:shadow-md">
                      <CardContent className="flex items-center gap-4">
                        {/* Icon */}
                        <div
                          className={cn(
                            "flex size-10 shrink-0 items-center justify-center rounded-full",
                            claim.entityType === "provider"
                              ? "bg-accent-soft text-accent-strong"
                              : "bg-primary-soft text-primary",
                          )}
                        >
                          {claim.entityType === "provider" ? (
                            <User className="size-5" aria-hidden />
                          ) : (
                            <Building2 className="size-5" aria-hidden />
                          )}
                        </div>

                        {/* Details */}
                        <div className="min-w-0 flex-1">
                          <p className="text-foreground truncate font-medium">{claim.entityName}</p>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                            <span className="text-muted-foreground">
                              {claim.entityType === "provider" ? (
                                <Trans>Doctor</Trans>
                              ) : (
                                <Trans>Facility</Trans>
                              )}
                            </span>
                            {claim.entityCity && (
                              <>
                                <span className="text-border">·</span>
                                <span className="text-muted-foreground">{claim.entityCity}</span>
                              </>
                            )}
                            <span className="text-border">·</span>
                            <span className="text-muted-foreground">
                              {new Date(claim.createdAt).toLocaleDateString(locale, {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                          </div>
                        </div>

                        {/* Status */}
                        <Badge variant={meta.variant} className="shrink-0">
                          {meta.icon}
                          {meta.label}
                        </Badge>

                        {/* An approved claim opens the workspace it unlocked (P16). */}
                        {workspaceLink && (
                          <ChevronRight
                            className="text-muted-foreground size-4 shrink-0"
                            aria-hidden
                          />
                        )}
                      </CardContent>
                      {workspaceLink && (
                        <Link
                          {...workspaceLink}
                          aria-label={t`Open workspace: ${entityName}`}
                          className="focus-visible:ring-ring/50 absolute inset-0 rounded-xl focus-visible:ring-3 focus-visible:outline-none"
                        />
                      )}
                    </Card>
                  </m.div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ClaimsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-11 w-28 rounded-full" />
        ))}
      </div>
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-[4.5rem] rounded-xl" />
        ))}
      </div>
    </div>
  );
}

/** Link props for the workspace an approved claim unlocked, else null. */
function claimWorkspaceLink(claim: MyClaim) {
  if (claim.status !== "approved") return null;
  if (claim.entityType === "provider" && claim.providerId) {
    return { to: "/account/provider" } as const;
  }
  if (claim.entityType === "facility" && claim.facilityId) {
    return {
      to: "/account/facility/$facilityId/dashboard",
      params: { facilityId: claim.facilityId },
    } as const;
  }
  if (claim.entityType === "business" && claim.businessId) {
    return {
      to: "/account/business/$businessId/billing",
      params: { businessId: claim.businessId },
    } as const;
  }
  return null;
}
